import React from 'react';
import { Box, Typography, TextField, Button, Popover } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import { responsiveTypography } from '../../themes/responsive';
import { MIN_ERP_LEAD_DAYS, startsTooSoonForErp, earliestErpStartDate } from '../../constants/experiments';

/**
 * Date editing popover for experiments
 * Allows users to change start date and duration in weeks
 */
const DateEditPopover = ({
  open,
  anchorEl,
  onClose,
  currentStartDate,
  currentEndDate,
  onSave,
  loading = false,
  t
}) => {
  // Get current language for date formatting
  const { i18n } = useTranslation();
  const [startDate, setStartDate] = React.useState(currentStartDate ? new Date(currentStartDate) : new Date());
  const [weeks, setWeeks] = React.useState(1);
  const [weeksInput, setWeeksInput] = React.useState('1');

  // Calculate weeks from current dates on mount
  React.useEffect(() => {
    if (currentStartDate && currentEndDate) {
      const start = new Date(currentStartDate);
      const end = new Date(currentEndDate);
      const diffTime = end.getTime() - start.getTime();
      const diffWeeks = Math.round(diffTime / (1000 * 60 * 60 * 24 * 7));
      const weekValue = diffWeeks > 0 ? diffWeeks : 1;
      setWeeks(weekValue);
      setWeeksInput(weekValue.toString());
      setStartDate(start);
    }
  }, [currentStartDate, currentEndDate]);

  // Calculate end date based on start date and weeks
  const calculateEndDate = (start, weekCount) => {
    const end = new Date(start);
    end.setDate(end.getDate() + (weekCount * 7));
    return end;
  };

  const endDate = calculateEndDate(startDate, weeks);

  const handleSave = () => {
    // Format dates in local time, not UTC
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const dates = {
      beginDate: formatLocalDate(startDate),
      endDate: formatLocalDate(endDate)
    };
    onSave(dates);
  };

  const handleCancel = () => {
    // Reset to original values
    setStartDate(currentStartDate ? new Date(currentStartDate) : new Date());
    if (currentStartDate && currentEndDate) {
      const start = new Date(currentStartDate);
      const end = new Date(currentEndDate);
      const diffTime = end.getTime() - start.getTime();
      const diffWeeks = Math.round(diffTime / (1000 * 60 * 60 * 24 * 7));
      const weekValue = diffWeeks > 0 ? diffWeeks : 1;
      setWeeks(weekValue);
      setWeeksInput(weekValue.toString());
    } else {
      setWeeks(1);
      setWeeksInput('1');
    }
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleCancel}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          border: `1px solid ${colors.border.light}`,
          boxShadow: `0 4px 20px ${colors.shadow.soft}`,
          p: '12px',
          minWidth: '280px'
        }
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Header */}
          <Typography sx={{
            ...responsiveTypography.small,
            fontWeight: 600,
            color: colors.text.primary
          }}>
{t('editDates')}
          </Typography>

          {/* Start Date Picker */}
          <Box>
            <Typography sx={{
              ...responsiveTypography.small,
              color: colors.text.secondary,
              mb: '4px'
            }}>
              {t('startDate')}
            </Typography>
            <DatePicker
              value={startDate}
              minDate={earliestErpStartDate()}
              onChange={(newDate) => setStartDate(newDate || new Date())}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '12px',
                      height: '32px'
                    }
                  }}
                />
              )}
            />
          </Box>

          {/* Duration in Weeks */}
          <Box>
            <Typography sx={{
              ...responsiveTypography.small,
              color: colors.text.secondary,
              mb: '4px'
            }}>
              {t('durationWeeks')}
            </Typography>
            <TextField
              type="number"
              value={weeksInput}
              onChange={(e) => {
                const value = e.target.value;
                setWeeksInput(value);
                
                // Update weeks value if it's a valid number
                const numValue = parseInt(value);
                if (!isNaN(numValue) && numValue >= 1 && numValue <= 52) {
                  setWeeks(numValue);
                } else if (value === '') {
                  setWeeks(1); // Default to 1 week for calculations
                }
              }}
              onBlur={(e) => {
                // On blur, ensure we have a valid value
                if (weeksInput === '' || parseInt(weeksInput) < 1) {
                  setWeeksInput('1');
                  setWeeks(1);
                }
              }}
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 52 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '12px',
                  height: '32px'
                }
              }}
            />
          </Box>

          {/* Preview */}
          <Box sx={{
            backgroundColor: colors.background.subtle,
            border: `1px solid ${colors.border.light}`,
            borderRadius: '4px',
            p: '8px'
          }}>
            <Typography sx={{
              ...responsiveTypography.small,
              color: colors.text.secondary,
              mb: '2px'
            }}>
              {t('preview')}:
            </Typography>
            <Typography sx={{
              ...responsiveTypography.small,
              color: colors.text.primary,
              fontWeight: 600
            }}>
              {t('from')} {startDate.toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { day: 'numeric', month: 'long' })} {t('to')} {endDate.toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { day: 'numeric', month: 'long' })}
            </Typography>
          </Box>

          {/* La regla del ERP sólo cuando la fecha elegida la incumple: el guardado la rechazaría */}
          {startsTooSoonForErp(startDate) && (
            <Typography sx={{
              ...responsiveTypography.small,
              color: '#d32f2f',
              fontWeight: 600,
              mb: '8px',
              display: 'block',
              whiteSpace: 'normal',
              maxWidth: '100%'
            }}>
              {t('erpLeadTimeRule', { days: MIN_ERP_LEAD_DAYS })}
            </Typography>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleCancel}
              size="small"
              sx={{
                color: colors.text.secondary,
                ...responsiveTypography.small,
                textTransform: 'none',
                minWidth: '60px'
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onMouseDown={(e) => {
                // Capture main scroll container position before save
                const mainScrollContainer = document.querySelector('.css-4z6nar');
                if (mainScrollContainer) {
                  window.savedColumnScroll = {
                    scrollTop: mainScrollContainer.scrollTop
                  };
                }
              }}
              onClick={handleSave}
              disabled={loading}
              size="small"
              variant="contained"
              sx={{
                backgroundColor: colors.action.primary,
                color: 'white',
                ...responsiveTypography.small,
                textTransform: 'none',
                minWidth: '60px',
                '&:hover': {
                  backgroundColor: colors.action.primaryDark || '#1976d2',
                  color: 'white'
                },
                '&:disabled': {
                  backgroundColor: colors.action.primary,
                  color: 'white',
                  opacity: 0.6
                }
              }}
            >
              {loading ? t('loading') : t('save')}
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>
    </Popover>
  );
};

export default DateEditPopover;